import React from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import { useTheme, buildUseStyles } from 'Components/ComponentLibrary/ExternalLibraries/Libraries/MaterialV4/styles';
import fr from 'facet-rest';
import ProfileSubpage from 'Views/Page/Content/Templates/Profile/Templates/ProfileSubpage';
import useMobileLayout from 'Hooks/useMobileLayout';
import MetaControl from 'Components/UI/Meta/MetaControl';
import { buildFacetDesignOverride, overrideReadOnly } from 'Packages/FacetRest/FacetDesignOverride';
import {
  FDBox,
  FDHeadlineText, FDPaper, FDStack,
} from '@facet-wealth/design';
import { PrimaryActionButtonRel } from 'Packages/FacetCore/Experimental/RelationshipComponents/PrimaryActionButtonRel';
import { LozengeLarge } from 'Packages/FacetDesign/Components';
import { Text } from 'Packages/FacetDesign/Components/Text';
import { useCancelChurn } from 'Hooks/useCancelChurn';
import useBoneBackgroundColor from 'Hooks/useBoneBackgroundColor';
import { root, mobile } from './subpageClasses';

const metaOverrideSet = buildFacetDesignOverride()
  .nameOverride('membershipActiveThrough', {
    _component: () => LozengeLarge,
    _customProps: { color: 'info' },
  }, overrideReadOnly)
  .nameOverride('membershipCanceledOn', {
    _component: () => LozengeLarge,
    _customProps: { color: 'error' },
  }, overrideReadOnly)
  .nameOverride('membershipTitle', {
    _component: () => Text,
    _customProps: { Component: FDHeadlineText, size: 'md', color: 'textSecondary' },
  }, overrideReadOnly);

const useStyles = buildUseStyles('CancelMembershipSubpage', (theme) => ({
  root: root(theme),
  mobile: mobile(theme),
}));

const CancelMembershipSubpage = ({
  className,
  resource,
}) => {
  const theme = useTheme();
  const classes = useStyles(theme);
  const profilePageRelationship = resource.relationship(fr.relationships.householdMembershipPage.name);
  const isMobile = useMobileLayout();
  const backLabel = profilePageRelationship?.meta('prompt');
  const backLink = profilePageRelationship?.link('related');
  useBoneBackgroundColor();

  const { onCancelChurn, isCancelingChurn } = useCancelChurn(resource.relationship(fr.relationships.requestChurnCancel.name));

  return (
    <ProfileSubpage
      className={classNames(classes.root, className, { [classes.mobile]: isMobile })}
      title={resource.attr('title')}
      backLabel={backLabel}
      backLink={backLink}
    >
      <FDStack direction={isMobile ? 'column' : 'row'} spacing={3} justifyContent="space-between" alignItems="flex-start" wrap>
        <FDBox flex={1}>
          <MetaControl resource={resource} attrName="subtitle" metaOverrideSet={metaOverrideSet} />
        </FDBox>
        <FDPaper padding={isMobile ? 3 : 4} flex={1} maxWidth={isMobile ? undefined : theme.dimensions.md}>
          <FDStack direction="column">
            <FDStack spacing={3} direction="column">
              <FDStack spacing={2} direction="column">
                <MetaControl attrName="membershipCanceledOn" resource={resource} metaOverrideSet={metaOverrideSet} />
                <MetaControl attrName="membershipActiveThrough" resource={resource} metaOverrideSet={metaOverrideSet} />
                <MetaControl attrName="membershipTitle" resource={resource} metaOverrideSet={metaOverrideSet} />
              </FDStack>
            </FDStack>
            <MetaControl attrName="membershipDescription" resource={resource} metaOverrideSet={metaOverrideSet} />
            <PrimaryActionButtonRel relationship={resource.relationship(fr.relationships.requestChurn.name)} />
            <PrimaryActionButtonRel relationship={resource.relationship(fr.relationships.requestChurnCancel.name)} overrideOnClick={onCancelChurn} loading={isCancelingChurn} />
            <PrimaryActionButtonRel relationship={resource.relationship(fr.relationships.needHelp.name)} />
          </FDStack>
        </FDPaper>
      </FDStack>
    </ProfileSubpage>
  );
};

CancelMembershipSubpage.defaultProps = {
  className: '',
};

CancelMembershipSubpage.propTypes = {
  className: PropTypes.string,
  resource: PropTypes.instanceOf(fr.Resource).isRequired,
};

export default CancelMembershipSubpage;
